import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAdminRsvps } from '../lib/api';
import type { RsvpRow, RsvpStatus } from '../lib/types';
import { RSVP_STATUSES } from '../lib/types';
import { RsvpTable } from '../components/admin/RsvpTable';
import { toCsv } from '../lib/csv';
import '../styles/admin.css';

export const Route = createFileRoute('/admin-9k4m2p7x3b')({
  component: AdminPage,
});

type Filter = 'all' | RsvpStatus;

const CSV_HEADERS = [
  'id', 'child_name', 'parent_name', 'parent_email', 'parent_phone',
  'rsvp_status', 'character_name', 'shirt_size', 'allergies', 'notes',
  'favor_ordered', 'favor_received', 'checked_in', 'admin_notes',
] as const;

const STATUS_LABEL: Record<RsvpStatus, string> = {
  coming: 'Coming',
  maybe: 'Maybe',
  not_coming: 'Not coming',
};

function AdminPage() {
  const [rsvps, setRsvps] = useState<RsvpRow[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function load() {
    setLoading(true);
    fetchAdminRsvps()
      .then((rows) => {
        if (mountedRef.current) {
          setRsvps(rows);
          setErr(null);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (mountedRef.current) {
          setErr(String(e?.message ?? e));
          setLoading(false);
        }
      });
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (filter === 'all' ? rsvps : rsvps.filter((r) => r.rsvp_status === filter)),
    [rsvps, filter],
  );

  function exportCsv() {
    const csv = toCsv(CSV_HEADERS, filtered as unknown as Record<string, unknown>[]);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const counts = {
    coming: rsvps.filter((r) => r.rsvp_status === 'coming').length,
    maybe: rsvps.filter((r) => r.rsvp_status === 'maybe').length,
    not_coming: rsvps.filter((r) => r.rsvp_status === 'not_coming').length,
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>RSVPs</h1>
      </div>

      <div className="admin-stats">
        <div className="admin-stat">
          <span className="admin-stat__label">Coming</span>
          <span className="admin-stat__value">{counts.coming}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Maybe</span>
          <span className="admin-stat__value">{counts.maybe}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Not Coming</span>
          <span className="admin-stat__value">{counts.not_coming}</span>
        </div>
        <div className="admin-stat">
          <span className="admin-stat__label">Total</span>
          <span className="admin-stat__value">{rsvps.length}</span>
        </div>
      </div>

      <div className="admin-toolbar">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as Filter)}
          disabled={loading}
        >
          <option value="all">All Statuses</option>
          {RSVP_STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <button onClick={exportCsv} disabled={loading}>Export CSV</button>
        <button onClick={load} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {err && <div className="admin-error">{err}</div>}

      {loading && rsvps.length === 0 ? (
        <div className="admin-empty">Loading…</div>
      ) : rsvps.length === 0 ? (
        <div className="admin-empty">No RSVPs yet.</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty">No RSVPs match this filter.</div>
      ) : (
        <RsvpTable rsvps={filtered} onChange={load} />
      )}
    </div>
  );
}
